// libraries
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

// ant design
import { Badge, Button, notification, Table, Tooltip, Typography } from 'antd';
import { UserOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { red, orange, green, blue } from '@ant-design/colors';

// chartjs
import { Chart as ChartJS, ArcElement, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// resources
import * as HLService from '../../../../../services/bb-api/hey-loyalty';
import { txt } from '../../../../../locales/i18n';
import { formatDate } from '../../../../../utils/date';
import { Spinner } from '../../../../shared/spinner';
import { DeleteModal } from '../../../../shared/delete-modal';

ChartJS.register(ArcElement, Legend);

export type HeyLoyaltyTabProps = {
	user: Record<any, any>;
};

type MemberStats = { clickCount: number; openCount: number; mailsSent: number };

export function HeyLoyaltyTab({ user }: HeyLoyaltyTabProps): JSX.Element {
	const [memberList, setMemberList] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [rejectedHlDeletions, setRejectedHlDeletions] = useState<any[]>([]);

	const getMember = useCallback(async () => {
		setLoading(true);

		try {
			const memberLists: any = await HLService.getMemberLists(user.id);
			setMemberList(memberLists);

			const initialMemberStats: MemberStats = { clickCount: 0, openCount: 0, mailsSent: 0 };
			const memberStats = memberLists.reduce((memberStats: MemberStats, listItem: any) => {
				memberStats.clickCount += listItem.click_count;
				memberStats.openCount += listItem.open_count;
				memberStats.mailsSent += listItem.sent_mail;
				return memberStats;
			}, initialMemberStats);

			setMemberStats(memberStats);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}, [user]);

	useEffect(() => {
		getMember();
	}, [getMember]);

	function getDataSource(member: Record<any, any>): Record<any, any>[] {
		const dataSource: Record<any, any>[] = [
			{
				id: txt('global.username'),
				heyLoyalty: member.firstname,
				boblberg: user.username,
				error: member.firstname.trim() !== user.username.trim(),
			},

			{
				id: 'Email',
				heyLoyalty: member.email,
				boblberg: user.email,
				error: member.email.trim() !== user.email.trim(),
			},

			{
				id: 'ID',
				heyLoyalty: member.userId,
				boblberg: user.id,
				error: Number(member.userId) !== Number(user.id),
			},

			{
				id: txt('global.birth year'),
				heyLoyalty: member.foedselsaar,
				boblberg: user.birth_year,
				error: Number(member.foedselsaar) !== Number(user.birth_year),
			},

			{
				id: txt('global.location'),
				heyLoyalty:
					member.municipality === 'inactive'
						? txt('global.inactive')
						: member.municipality,
				boblberg: user.municipality,
				error: member.municipality !== user.municipality,
				warning: member.municipality === 'inactive',
			},

			{
				id: 'Status',
				heyLoyalty: member.status.status,
				boblberg: '-',
				error: member.status.status !== 'active',
			},
		];

		return dataSource;
	}

	function getEmailStatusBadgeProps(status: string | undefined) {
		if (status === 'hard_bounce') return { count: 'HARD BOUNCE', color: 'red' };
		if (status === 'soft_bounce') return { count: 'SOFT BOUNCE', color: 'orange' };
		return { count: txt('heyloyalty.active').toUpperCase(), color: 'green' };
	}

	function getRowClassName(record: Record<any, any>): string {
		if (record.warning) return 'orange-row-highlight';
		if (record.error) return 'red-row-highlight';
		return '';
	}

	function getOpenRateAverage(): number {
		if (memberList === null) return 0;

		const openRateSum = memberList.reduce((openRateSum, { open_rate }) => {
			openRateSum += open_rate;
			return openRateSum;
		}, 0);

		const openRateAverage = openRateSum / memberList.length;
		return Number(openRateAverage.toFixed(2));
	}

	function getClickRateAverage(): number {
		if (memberList === null) return 0;

		const openRateSum = memberList.reduce((openRateSum, { click_rate }) => {
			openRateSum += click_rate;
			return openRateSum;
		}, 0);

		const openRateAverage = openRateSum / memberList.length;
		return Number(openRateAverage.toFixed(2));
	}

	if (loading) {
		return (
			<div style={{ marginBlock: '25vh' }}>
				<Spinner text={txt('global.getting data')} />
			</div>
		);
	}

	if (memberList[0] === undefined || memberStats === null) {
		const yesterday = moment(Date.now()).subtract(1, 'days');
		yesterday.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

		if (moment(user.created) < yesterday) {
			return (
				<StyledHeyLoyaltyTab>
					<Card title="Information" type="error">
						{txt('heyloyalty.not found error')}
					</Card>
				</StyledHeyLoyaltyTab>
			);
		}

		return (
			<StyledHeyLoyaltyTab>
				<Card title="Information" type="warning">
					{txt('heyloyalty.not found warning')}
				</Card>
			</StyledHeyLoyaltyTab>
		);
	}

	function getFirstCreated() {
		const memberListSorted = memberList.sort((a, b) => {
			if (a.created_at < b.created_at) return -1;
			if (a.created_at > b.created_at) return 1;
			return 0;
		});

		const [memberListItem] = memberListSorted;
		return memberListItem.created_at;
	}

	function getLastClickDate() {
		const clickedLists = memberList.filter((listItem) => listItem?.last_click !== undefined);

		const memberListSorted = clickedLists.sort((a, b) => {
			if (a.last_click < b.last_click) return -1;
			if (a.last_click > b.last_click) return 1;
			return 0;
		});

		const [memberListItem] = memberListSorted;
		return memberListItem?.last_click;
	}

	function getLastOpenDate() {
		const openedLists = memberList.filter((listItem) => listItem?.last_open !== undefined);

		const memberListSorted = openedLists.sort((a, b) => {
			if (a.last_open < b.last_open) return -1;
			if (a.last_open > b.last_open) return 1;
			return 0;
		});

		const [memberListItem] = memberListSorted;
		return memberListItem?.last_open;
	}

	async function handleDelete(): Promise<void> {
		const rejectedUris = await HLService.deleteMember(user.id);

		if (rejectedUris.length !== 0) {
			setRejectedHlDeletions(rejectedUris);
			throw new Error('User not deleted');
		}

		await getMember();
	}

	return (
		<StyledHeyLoyaltyTab>
			<div className="email-label-container">
				<Badge showZero {...getEmailStatusBadgeProps(memberList[0].status.email)}>
					<Typography.Text className="email-label" ellipsis>
						{user.email}
					</Typography.Text>
				</Badge>

				<div className="sub-caption-container">
					<Tooltip title={txt('global.created')}>
						<Typography.Text type="secondary">
							{formatDate(getFirstCreated())}
						</Typography.Text>
					</Tooltip>

					<Button
						danger
						type="primary"
						size="small"
						onClick={() => setShowDeleteModal(true)}
					>
						{txt('global.delete')}
					</Button>
				</div>
			</div>

			<div className="campagne-container">
				<Badge showZero count={memberStats?.mailsSent ?? 0} color="blue">
					<Typography.Text type="secondary" className="campagne-action-label">
						{txt('heyloyalty.mails sent')}
					</Typography.Text>
				</Badge>

				<Badge showZero count={memberStats?.openCount ?? 0} color="blue">
					<Typography.Text type="secondary" className="campagne-action-label">
						{txt('heyloyalty.mails opened')}
					</Typography.Text>
				</Badge>

				<Badge showZero count={memberStats?.clickCount ?? 0} color="blue">
					<Typography.Text type="secondary" className="campagne-action-label">
						{txt('heyloyalty.mail clicks')}
					</Typography.Text>
				</Badge>
			</div>

			<div className="diagrams-container">
				<DoughnutChart
					caption={txt('heyloyalty.open rate')}
					value={getOpenRateAverage()}
					lastActivity={getLastOpenDate()}
				/>

				<DoughnutChart
					caption={txt('heyloyalty.click rate')}
					value={getClickRateAverage()}
					lastActivity={getLastClickDate()}
				/>
			</div>

			{memberList.map((member: any) => (
				<div key={member.id}>
					<Typography.Title level={4} className="table-title-header-container">
						<Tooltip title={txt('heyloyalty.open hl list')}>
							<Typography.Link
								href={`https://app.heyloyalty.com/lists/${member.listId}/members`}
								target="_blank"
							>
								{member.listName}
							</Typography.Link>
						</Tooltip>

						<Tooltip title={txt('heyloyalty.open hl member')}>
							<Typography.Link
								href={`https://app.heyloyalty.com/lists/${member.listId}/members/${member.id}`}
								target="_blank"
							>
								<UserOutlined />
							</Typography.Link>
						</Tooltip>
					</Typography.Title>

					<Table
						rowKey="id"
						loading={loading}
						dataSource={getDataSource(member)}
						pagination={{ position: [], pageSize: 200 }}
						sticky
						rowClassName={getRowClassName}
					>
						<Table.Column
							dataIndex="id"
							title={txt('global.name')}
							width="10ch"
							ellipsis
						/>
						<Table.Column
							dataIndex="heyLoyalty"
							title="Heyloyalty"
							width="10ch"
							ellipsis
						/>
						<Table.Column dataIndex="boblberg" title="Boblberg" width="10ch" ellipsis />
					</Table>
				</div>
			))}

			<DeleteModal
				onConfirm={handleDelete}
				useVisible={[showDeleteModal, setShowDeleteModal]}
				onDismiss={() => setRejectedHlDeletions([])}
			>
				{rejectedHlDeletions.length !== 0 && (
					<StyledDeleteModelContent>
						<h3>{txt('user.hl deletion error header')}</h3>
						<p>{txt('user.hl deletion error info')}</p>

						<p>
							<i>{txt('user.hl deletion error notice')}</i>
						</p>

						<ul>
							{rejectedHlDeletions.map(({ id, listId, listName }) => (
								<li key={listId}>
									<Typography.Link
										href={`https://app.heyloyalty.com/lists/${listId}/members/${id}`}
										target="_blank"
										rel="noreferrer"
										ellipsis
									>
										{listName}
									</Typography.Link>
								</li>
							))}
						</ul>
					</StyledDeleteModelContent>
				)}
			</DeleteModal>
		</StyledHeyLoyaltyTab>
	);
}

type DoughnutChartProps = {
	caption: string;
	value: number;
	lastActivity: Date;
};

function DoughnutChart({ caption, value, lastActivity }: DoughnutChartProps) {
	const data = {
		labels: [caption, ''],
		datasets: [
			{
				data: [value ?? 0, 100 - value ?? 0],
				backgroundColor: ['rgba(54, 162, 235, 0.2)', 'rgba(150, 150, 150, 0.2)'],
				borderColor: ['rgba(54, 162, 235, 1)', 'rgba(150, 150, 150, 0)'],
				borderWidth: 1,
			},
		],
	};

	return (
		<StyledDoughnutChart>
			<div className="chart-container">
				<Doughnut
					data={data}
					options={{
						plugins: { legend: { display: false }, tooltip: { enabled: false } },
					}}
				/>
				<Typography.Text type="secondary" className="chart-value">
					{value ?? 0}%
				</Typography.Text>
			</div>

			<div className="caption-container">
				<Typography.Text className="chart-caption">{caption}</Typography.Text>
				<Typography.Text className="last-activity">
					<Typography.Text type="secondary">
						{txt('heyloyalty.last activity')}
					</Typography.Text>{' '}
					<br />
					<Typography.Text>
						{lastActivity ? formatDate(lastActivity) : '-'}
					</Typography.Text>
				</Typography.Text>
			</div>
		</StyledDoughnutChart>
	);
}

type CardProps = {
	title: string;
	children: string | JSX.Element | JSX.Element[];
	type: 'info' | 'succuss' | 'warning' | 'error';
};

function Card(props: CardProps): JSX.Element {
	function getColor() {
		if (props.type === 'succuss') return green;
		if (props.type === 'warning') return orange;
		if (props.type === 'error') return red;
		return blue;
	}

	return (
		<StyledCard color={getColor()}>
			<h3>
				{props.title} <InfoCircleOutlined />
			</h3>

			<p>{props.children}</p>
		</StyledCard>
	);
}

type StyledCardProps = {
	color: any;
};

const StyledCard = styled.div<StyledCardProps>`
	color: ${({ color }) => color[8]};
	background-color: ${({ color }) => color[1]};
	padding: 1rem;
	border-radius: 1rem;
	box-shadow: 0 1ch 2ch #eee;
	max-width: 650px;

	h3 {
		color: ${({ color }) => color.primary};
	}
`;

const StyledDoughnutChart = styled.div`
	font-size: 1em;
	display: flex;
	flex-direction: column;
	align-items: center;
	flex-wrap: wrap;
	gap: 2ch;

	.caption-container {
		display: flex;
		flex-direction: column;
		text-align: center;

		.chart-caption {
			font-size: 1.6em;
		}
	}

	.chart-container {
		position: relative;
		height: 15em;
		width: 15em;

		display: flex;
		flex-direction: column;
		align-items: center;

		.chart-value {
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
			font-size: 2em;
		}
	}
`;

const StyledDeleteModelContent = styled.div`
	margin-top: 2ch;

	h3 {
		color: ${red.primary};
	}
`;

const StyledHeyLoyaltyTab = styled.section`
	display: flex;
	align-items: center;
	flex-direction: column;
	justify-content: space-evenly;
	gap: 4ch;

	.email-label-container {
		margin-top: 1ch;
		display: flex;
		flex-direction: column;
		align-items: center;

		.email-label {
			font-size: 2rem;
		}
	}

	.sub-caption-container {
		display: flex;
		gap: 2ch;
		justify-content: center;
		align-items: center;
	}

	.diagrams-container {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 6ch;
	}

	.campagne-container {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		justify-content: center;
		gap: 6ch;

		.campagne-action-label {
			font-size: 1.4rem;
		}

		.hl-link-container {
			display: flex;
			gap: 2ch;

			font-size: 1.4em;
		}
	}

	tr.ant-table-row.orange-row-highlight {
		td {
			background: ${orange[0]};
		}

		td:first-child {
			border-left: 3px solid ${orange[4]};
		}
	}

	tr.ant-table-row.red-row-highlight {
		td {
			background: ${red[0]};
		}

		td:first-child {
			border-left: 3px solid ${red[4]};
		}
	}

	.table-title-header-container {
		margin: 1ch;
		display: flex;
		align-items: center;
		gap: 1ch;
	}
`;
